import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Paperclip, Trash2, UploadCloud } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal } from "@/components/motion";
import { PageHero } from "@/pages/ServicesPage";
import { BUDGETS, CONTACT_METHODS, IMAGES, PROPERTY_SIZES, SERVICES, START_DATES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  full_name: "", email: "", phone: "", address: "",
  property_type: "", property_size: "", services: [], start_date: "",
  budget_range: "", description: "", contact_method: "", consent: false,
};

function Pills({ options, values, onToggle, multi, name }) {
  return (
    <div className="flex flex-wrap gap-2" role={multi ? "group" : "radiogroup"} aria-label={name}>
      {options.map((opt) => {
        const selected = multi ? values.includes(opt) : values === opt;
        return (
          <button
            key={opt}
            type="button"
            data-testid={`pill-${name}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
            data-selected={selected}
            aria-pressed={selected}
            onClick={() => onToggle(opt)}
            className="pill-option"
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

export default function QuotePage() {
  const [form, setForm] = useState(initial);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const startedAt = useRef(Date.now());
  const honeypot = useRef("");
  const fileInput = useRef(null);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const toggleService = (s) =>
    set("services", form.services.includes(s) ? form.services.filter((x) => x !== s) : [...form.services, s]);

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is over 10 MB.`);
        continue;
      }
      const data = new FormData();
      data.append("file", file);
      try {
        const res = await axios.post(`${API}/uploads`, data);
        setAttachments((a) => [...a, { path: res.data.path, filename: res.data.filename }]);
      } catch (e) {
        toast.error(e.response?.data?.detail || `Could not upload ${file.name}.`);
      }
    }
    setUploading(false);
    if (fileInput.current) fileInput.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.consent) { setError("Please accept the privacy consent to continue."); return; }
    if (!form.services.length) { setError("Please select at least one service."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, { ...form, attachments, consent: form.consent, website: honeypot.current, started_at: startedAt.current });
      setSubmitted(true);
      toast.success("Your quote request has been received.");
      window.scrollTo({ top: 0 });
    } catch (err) {
      const msg = err.response?.data?.detail || "Something went wrong. Please try again or call us directly.";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="quote-page">
      <SEO
        title="Request a Private Landscaping Quote | Calgary, Springbank & Bearspaw"
        description="Tell us about your dream backyard. Request a private quote for luxury landscaping, new-build landscaping and estate outdoor living in Calgary, Springbank and Bearspaw."
      />
      <PageHero
        testid="quote-hero"
        eyebrow="Private Quote"
        title="Tell Us About Your Dream Backyard."
        subtitle="A few thoughtful details help our team prepare the right plan for your property."
        image={IMAGES.bonfireLake}
      />

      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-8">
            {submitted ? (
              <Reveal>
                <div data-testid="quote-success" className="border border-[#C5A059]/50 bg-white p-10 sm:p-14 text-center">
                  <CheckCircle2 size={44} strokeWidth={1.2} className="mx-auto text-[#6B7C6E]" aria-hidden="true" />
                  <h2 className="mt-6 font-display text-3xl sm:text-4xl font-light">Thank You.</h2>
                  <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-[#4A524C]">
                    We'll review your property, vision and project requirements and contact you within two business days.
                  </p>
                </div>
              </Reveal>
            ) : (
              <form onSubmit={submit} data-testid="quote-form" className="border border-[#E0D8CC] bg-white p-7 sm:p-12" noValidate={false}>
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="absolute left-[-9999px] top-0 h-0 w-0 opacity-0"
                  onChange={(e) => { honeypot.current = e.target.value; }}
                />

                <p className="eyebrow !text-[#8B5A2B]">01 — You</p>
                <div className="mt-5 grid gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="q-name" className="field-label">Full Name *</label>
                    <input id="q-name" data-testid="quote-name-input" required className="field-input" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} autoComplete="name" />
                  </div>
                  <div>
                    <label htmlFor="q-email" className="field-label">Email *</label>
                    <input id="q-email" data-testid="quote-email-input" required type="email" className="field-input" value={form.email} onChange={(e) => set("email", e.target.value)} autoComplete="email" />
                  </div>
                  <div>
                    <label htmlFor="q-phone" className="field-label">Phone Number *</label>
                    <input id="q-phone" data-testid="quote-phone-input" required type="tel" className="field-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} autoComplete="tel" />
                  </div>
                  <div>
                    <label htmlFor="q-address" className="field-label">Property Address or Community *</label>
                    <input id="q-address" data-testid="quote-address-input" required placeholder="e.g. Springbank, Bearspaw, Aspen Woods…" className="field-input" value={form.address} onChange={(e) => set("address", e.target.value)} />
                  </div>
                </div>

                <div className="gold-rule my-10" />
                <p className="eyebrow !text-[#8B5A2B]">02 — The Property</p>
                <div className="mt-5 space-y-8">
                  <div>
                    <span className="field-label">New Build or Existing Property *</span>
                    <Pills name="property-type" options={["New build", "Existing property"]} values={form.property_type} onToggle={(v) => set("property_type", v)} />
                  </div>
                  <div>
                    <span className="field-label">Approximate Property Size *</span>
                    <Pills name="property-size" options={PROPERTY_SIZES} values={form.property_size} onToggle={(v) => set("property_size", v)} />
                  </div>
                </div>

                <div className="gold-rule my-10" />
                <p className="eyebrow !text-[#8B5A2B]">03 — The Project</p>
                <div className="mt-5 space-y-8">
                  <div>
                    <span className="field-label">Services Required *</span>
                    <Pills name="services" multi options={SERVICES.map((s) => s.title)} values={form.services} onToggle={toggleService} />
                  </div>
                  <div>
                    <span className="field-label">Desired Project Start Date *</span>
                    <Pills name="start-date" options={START_DATES} values={form.start_date} onToggle={(v) => set("start_date", v)} />
                  </div>
                  <div>
                    <span className="field-label">Estimated Investment Range *</span>
                    <Pills name="budget" options={BUDGETS} values={form.budget_range} onToggle={(v) => set("budget_range", v)} />
                  </div>
                  <div>
                    <label htmlFor="q-description" className="field-label">Project Description</label>
                    <textarea
                      id="q-description"
                      data-testid="quote-description-input"
                      rows={5}
                      className="field-input resize-y"
                      placeholder="Tell us how you want to live outdoors — entertaining, quiet mornings, space for children, a pool, mature trees…"
                      value={form.description}
                      onChange={(e) => set("description", e.target.value)}
                    />
                  </div>
                  <div>
                    <span className="field-label">Inspiration or Plan Upload</span>
                    <input
                      ref={fileInput}
                      type="file"
                      multiple
                      accept="image/jpeg,image/png,image/webp,application/pdf"
                      className="hidden"
                      data-testid="quote-file-input"
                      onChange={(e) => handleFiles(Array.from(e.target.files))}
                    />
                    <button
                      type="button"
                      data-testid="quote-upload-btn"
                      onClick={() => fileInput.current?.click()}
                      disabled={uploading}
                      className="flex w-full items-center justify-center gap-3 border border-dashed border-[#C5A059]/60 bg-[#FAF7F2] px-6 py-8 text-sm text-[#4A524C] transition-colors hover:bg-[#F2EDE4]"
                    >
                      {uploading ? <Loader2 size={18} className="animate-spin" /> : <UploadCloud size={18} className="text-[#C5A059]" />}
                      {uploading ? "Uploading…" : "Add inspiration photos or plans (JPG, PNG, WEBP, PDF — up to 10 MB each)"}
                    </button>
                    {attachments.length > 0 && (
                      <ul className="mt-3 space-y-2" data-testid="quote-attachments-list">
                        {attachments.map((a) => (
                          <li key={a.path} className="flex items-center justify-between border border-[#E0D8CC] bg-[#FAF7F2] px-4 py-2.5 text-sm">
                            <span className="flex items-center gap-2 truncate"><Paperclip size={14} className="shrink-0 text-[#C5A059]" /> {a.filename}</span>
                            <button type="button" aria-label={`Remove ${a.filename}`} data-testid="quote-attachment-remove" onClick={() => setAttachments((list) => list.filter((x) => x.path !== a.path))} className="p-1 text-[#758077] hover:text-[#9c3d33]">
                              <Trash2 size={15} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                <div className="gold-rule my-10" />
                <p className="eyebrow !text-[#8B5A2B]">04 — How We Reach You</p>
                <div className="mt-5">
                  <span className="field-label">Preferred Contact Method *</span>
                  <Pills name="contact-method" options={CONTACT_METHODS} values={form.contact_method} onToggle={(v) => set("contact_method", v)} />
                </div>

                <label className="mt-8 flex cursor-pointer items-start gap-3 text-sm text-[#4A524C]" data-testid="quote-consent-label">
                  <input
                    type="checkbox"
                    data-testid="quote-consent-checkbox"
                    checked={form.consent}
                    onChange={(e) => set("consent", e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-[#8B5A2B]"
                  />
                  <span>I consent to Estate Outdoors Calgary contacting me about my project and storing my details as described in the Privacy Policy. *</span>
                </label>

                {error && (
                  <p data-testid="quote-form-error" role="alert" className="mt-6 border border-[#9c3d33]/40 bg-[#9c3d33]/5 px-4 py-3 text-sm text-[#9c3d33]">
                    {error}
                  </p>
                )}

                <button type="submit" data-testid="quote-submit-btn" disabled={submitting} className="btn-primary mt-9 w-full disabled:opacity-60">
                  {submitting ? (<><Loader2 size={16} className="animate-spin" /> Sending…</>) : "Request My Consultation"}
                </button>
              </form>
            )}
          </div>

          <aside className="lg:col-span-4">
            <Reveal delay={0.1} className="lg:sticky lg:top-28 space-y-6">
              <div className="bg-[#1C201D] p-8 text-[#FAF7F2] grain relative">
                <h2 className="font-display text-2xl font-normal">What Happens Next</h2>
                <ol className="mt-6 space-y-5 text-sm leading-relaxed text-[#B9BDB4]">
                  <li className="flex gap-4"><span className="font-display italic text-[#C5A059]">01</span> We personally review your property, vision and requirements.</li>
                  <li className="flex gap-4"><span className="font-display italic text-[#C5A059]">02</span> Within two business days, we reach out by your preferred method.</li>
                  <li className="flex gap-4"><span className="font-display italic text-[#C5A059]">03</span> We walk your property together and present a tailored design and clear proposal.</li>
                </ol>
              </div>
              <div className="border border-[#E0D8CC] bg-[#F2EDE4] p-8">
                <h2 className="font-display text-2xl font-normal">Prefer to Talk?</h2>
                <p className="mt-3 text-sm leading-relaxed text-[#4A524C]">We're happy to begin with a conversation about your property.</p>
                <a data-testid="quote-call-link" href="tel:+15879690569" className="btn-outline-dark mt-5 w-full">Call (587) 969-0569</a>
              </div>
            </Reveal>
          </aside>
        </div>
      </section>
    </div>
  );
}
